import type { BundleComponent, BundleEditor, BundleStockMode } from "@cleanbudget/contract/bundle";
import type { ProductEditor } from "@cleanbudget/contract/product";
import { Alert, AlertDescription, AlertTitle } from "@cleanbudget/ui/components/alert";
import { Button } from "@cleanbudget/ui/components/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@cleanbudget/ui/components/card";
import { Field, FieldLabel } from "@cleanbudget/ui/components/field";
import { Input } from "@cleanbudget/ui/components/input";
import { useToast } from "@cleanbudget/ui/components/toast";
import { cn } from "@cleanbudget/ui/lib/utils";
import { IconAlertTriangle, IconInfoCircle, IconPlus, IconTrash } from "@tabler/icons-react";
import {
	keepPreviousData,
	useMutation,
	useQuery,
	useQueryClient,
	useSuspenseQuery,
} from "@tanstack/react-query";
import { useDeferredValue, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { match } from "ts-pattern";
import { type Column, DataTable } from "@/components/data-table/data-table";
import { StatusBadge } from "@/components/status-badge";
import { apiErrorTag } from "@/lib/api-error";
import { formatCents } from "@/lib/money";
import { orpc } from "@/lib/orpc";
import type { Save } from "./fields";

/** The "Onderdelen" card's data: one query, which every write replaces with the editor it answers. */
export const bundleQuery = (productId: string) => orpc.bundle.get.queryOptions({ input: { productId } });

/**
 * "Onderdelen" (#60, #64), for a product of kind bundle: its stock mode, its components (a variant
 * of another product and how many), and a search to add one. The bundle's price is its own, set in
 * Basis; the card shows what the parts cost apart. Every write answers the whole `BundleEditor`.
 */
export function BundleSection({ product }: { product: ProductEditor }) {
	const { t } = useTranslation("bundle");
	const productId = product.id;
	const { data: bundle } = useSuspenseQuery(bundleQuery(productId));
	const writes = useBundleWrites(productId);
	const run = useRunBundleWrite();
	const bundlePrice = product.consumerPrice ?? product.variants[0]?.consumerPrice ?? 0;
	// A component that is not for sale (inactive or deleted) makes a composed bundle unsellable (#60);
	// a prepacked one still sells on its own stock (#64), so it only gets a note.
	const notForSale = bundle.components.filter((component) => !component.active);
	const notForSaleNames = {
		count: notForSale.length,
		names: listFormat.format(notForSale.map(componentName)),
	};

	const columns: Column<BundleComponent>[] = [
		{
			id: "name",
			header: t("components.column.name"),
			cell: (component) => (
				<span className="flex flex-col items-start gap-1 whitespace-normal">
					<span className="font-medium">{componentName(component)}</span>
					<span className="font-mono text-xs text-muted-foreground">{component.sku}</span>
					{component.active ? null : <StatusBadge tone="attention">{t("components.inactive")}</StatusBadge>}
					{/* On a phone the availability and price columns are hidden; show them here. */}
					<span className="text-xs text-muted-foreground md:hidden">
						{t("components.column.available")}: {component.available} ·{" "}
						{t("components.priceEach", { price: formatCents(component.consumerPrice) })}
					</span>
				</span>
			),
		},
		{
			id: "quantity",
			header: t("components.column.quantity"),
			cell: (component) => (
				<QuantityInput
					value={component.quantity}
					label={t("components.quantityOf", { name: componentName(component) })}
					onCommit={(quantity) =>
						run(writes.updateComponent.mutateAsync({ productId, variantId: component.variantId, quantity }))
					}
				/>
			),
		},
		{
			id: "available",
			header: t("components.column.available"),
			secondary: true,
			align: "right",
			cell: (component) => <span className="tabular-nums">{component.available}</span>,
		},
		{
			id: "price",
			header: t("components.column.price"),
			secondary: true,
			align: "right",
			cell: (component) => <span className="tabular-nums">{formatCents(component.consumerPrice)}</span>,
		},
		{
			id: "actions",
			header: <span className="sr-only">{t("components.column.actions")}</span>,
			align: "right",
			cell: (component) => (
				<Button
					variant="ghost"
					size="icon-sm"
					onClick={() =>
						void run(writes.removeComponent.mutateAsync({ productId, variantId: component.variantId }))
					}
					aria-label={t("components.remove", { name: componentName(component) })}
				>
					<IconTrash aria-hidden />
				</Button>
			),
		},
	];

	return (
		<Card>
			<CardHeader>
				<CardTitle>{t("components.title")}</CardTitle>
				<CardDescription>{t("components.description")}</CardDescription>
			</CardHeader>
			<CardContent className="flex flex-col gap-6">
				{notForSale.length === 0 ? null : bundle.stockMode === "composed" ? (
					<Alert variant="destructive">
						<IconAlertTriangle aria-hidden />
						<AlertTitle>{t("components.notForSaleTitle")}</AlertTitle>
						<AlertDescription>{t("components.notForSale", notForSaleNames)}</AlertDescription>
					</Alert>
				) : (
					<Alert>
						<IconInfoCircle aria-hidden />
						<AlertDescription>{t("components.inactivePrepacked", notForSaleNames)}</AlertDescription>
					</Alert>
				)}
				<StockModeChoice
					bundle={bundle}
					switching={writes.setStockMode.isPending}
					onChange={(stockMode) => void run(writes.setStockMode.mutateAsync({ productId, stockMode }))}
				/>
				<div className="overflow-x-auto">
					<DataTable
						label={t("components.title")}
						columns={columns}
						rows={bundle.components}
						rowKey={(component) => component.variantId}
						empty={<p className="text-muted-foreground">{t("components.empty")}</p>}
					/>
				</div>
				{bundle.components.length > 0 ? (
					<p className="text-sm text-muted-foreground">
						{t("components.summary", {
							parts: formatCents(bundle.componentsConsumerPrice),
							bundle: formatCents(bundlePrice),
						})}{" "}
						{bundle.componentsConsumerPrice > bundlePrice
							? t("components.cheaper", { amount: formatCents(bundle.componentsConsumerPrice - bundlePrice) })
							: null}
					</p>
				) : null}
				<AddComponent
					productId={productId}
					onAdd={(variantId) => run(writes.addComponent.mutateAsync({ productId, variantId, quantity: 1 }))}
				/>
			</CardContent>
		</Card>
	);
}

const listFormat = new Intl.ListFormat("nl", { type: "conjunction" });

/** The variant's name as the editor shows it: the product's, with the value names after it. */
const componentName = (component: { productName: string; variantName: string }) =>
	component.variantName === component.productName
		? component.productName
		: `${component.productName} – ${component.variantName}`;

/** Samengesteld or voorverpakt, as two radio buttons with what each means, and the availability. */
function StockModeChoice({
	bundle,
	switching,
	onChange,
}: {
	bundle: BundleEditor;
	/** A switch is on its way: the availability shown is still the old mode's until it answers. */
	switching: boolean;
	onChange: (stockMode: BundleStockMode) => void;
}) {
	const { t } = useTranslation("bundle");
	const name = useId();
	const modes = [
		{ value: "composed", label: t("stockMode.composed"), description: t("stockMode.composedDescription") },
		{ value: "prepacked", label: t("stockMode.prepacked"), description: t("stockMode.prepackedDescription") },
	] as const;
	return (
		<fieldset className="flex flex-col gap-3">
			<legend className="mb-3 text-sm font-medium">{t("stockMode.label")}</legend>
			<div className="grid gap-3 sm:grid-cols-2">
				{modes.map((mode) => (
					<label
						key={mode.value}
						className={cn(
							"flex cursor-pointer items-start gap-3 rounded-xl p-3 ring-1 ring-foreground/10",
							bundle.stockMode === mode.value && "bg-muted ring-foreground/30",
						)}
					>
						<input
							type="radio"
							name={name}
							value={mode.value}
							checked={bundle.stockMode === mode.value}
							onChange={() => onChange(mode.value)}
							className="mt-1 accent-primary"
						/>
						<span className="flex flex-col gap-1">
							<span className="text-sm font-medium">{mode.label}</span>
							<span className="text-sm text-muted-foreground">{mode.description}</span>
						</span>
					</label>
				))}
			</div>
			<p
				className={cn("text-sm", switching && "text-muted-foreground")}
				aria-live="polite"
				aria-busy={switching}
			>
				{t("stockMode.available", { count: bundle.available })}
			</p>
		</fieldset>
	);
}

/** A quantity that saves itself on Enter or when it loses focus; 1 to 999. */
function QuantityInput({ value, label, onCommit }: { value: number; label: string; onCommit: Save<number> }) {
	const { t } = useTranslation("bundle");
	const toast = useToast();
	/** What is typed and not yet saved; `null` shows `value`. */
	const [draft, setDraft] = useState<string | null>(null);
	const commit = async () => {
		if (draft === null) return;
		const quantity = Number(draft.trim());
		if (quantity === value) return setDraft(null);
		if (!Number.isInteger(quantity) || quantity < 1 || quantity > 999) {
			toast.add({ title: t("components.quantityInvalid"), priority: "high" });
			return setDraft(null);
		}
		await onCommit(quantity);
		setDraft(null);
	};
	return (
		<Input
			className="w-20 tabular-nums"
			value={draft ?? String(value)}
			onChange={(event) => setDraft(event.target.value)}
			onBlur={() => void commit()}
			onKeyDown={(event) => {
				if (event.key === "Enter") event.currentTarget.blur();
				if (event.key === "Escape") setDraft(null);
			}}
			inputMode="numeric"
			autoComplete="off"
			aria-label={label}
		/>
	);
}

/** A search over the variants of standard products, each result with a button that adds it. */
function AddComponent({ productId, onAdd }: { productId: string; onAdd: Save<string> }) {
	const { t } = useTranslation("bundle");
	const id = useId();
	const [search, setSearch] = useState("");
	const deferred = useDeferredValue(search.trim());
	const candidates = useQuery({
		...orpc.bundle.candidates.queryOptions({ input: { productId, search: deferred } }),
		enabled: deferred !== "",
		placeholderData: keepPreviousData,
	});
	const results = deferred === "" ? [] : (candidates.data ?? []);

	return (
		<Field>
			<FieldLabel htmlFor={id}>{t("add.label")}</FieldLabel>
			<Input
				id={id}
				type="search"
				value={search}
				onChange={(event) => setSearch(event.target.value)}
				placeholder={t("add.placeholder")}
				autoComplete="off"
			/>
			{deferred !== "" && candidates.isSuccess && results.length === 0 ? (
				<p className="text-sm text-muted-foreground">{t("add.none")}</p>
			) : null}
			{results.length > 0 ? (
				<ul
					className="flex flex-col divide-y rounded-xl ring-1 ring-foreground/10"
					aria-label={t("add.label")}
				>
					{results.map((candidate) => (
						<li key={candidate.variantId} className="flex items-center justify-between gap-3 px-3 py-2">
							<span className="flex min-w-0 flex-col">
								<span className="truncate text-sm font-medium">{componentName(candidate)}</span>
								<span className="truncate text-xs text-muted-foreground">
									<span className="font-mono">{candidate.sku}</span> · {formatCents(candidate.consumerPrice)}
								</span>
							</span>
							{candidate.inBundle ? (
								<span className="shrink-0 text-sm text-muted-foreground">{t("add.inBundle")}</span>
							) : (
								<Button
									variant="outline"
									size="sm"
									className="shrink-0"
									onClick={async () => {
										await onAdd(candidate.variantId);
										setSearch("");
									}}
									aria-label={`${t("add.add")}: ${componentName(candidate)}`}
								>
									<IconPlus aria-hidden />
									{t("add.add")}
								</Button>
							)}
						</li>
					))}
				</ul>
			) : null}
		</Field>
	);
}

/**
 * Every write the card makes; each answers the whole `BundleEditor`, which replaces the cached one.
 * They share a scope, so they reach the server one after another, in the order they were made.
 *
 * The stock mode shows at once (#64): the radio buttons follow the cached editor, so without that
 * a click did nothing visible until the server had answered. A refusal puts the old editor back.
 */
function useBundleWrites(productId: string) {
	const queryClient = useQueryClient();
	const key = bundleQuery(productId).queryKey;
	const scope = { id: `bundle:${productId}` };
	const options = {
		scope,
		onSuccess: (bundle: BundleEditor) => {
			queryClient.setQueryData(key, bundle);
			void queryClient.invalidateQueries({ queryKey: orpc.bundle.candidates.key() });
		},
	};
	return {
		addComponent: useMutation(orpc.bundle.addComponent.mutationOptions(options)),
		updateComponent: useMutation(orpc.bundle.updateComponent.mutationOptions(options)),
		removeComponent: useMutation(orpc.bundle.removeComponent.mutationOptions(options)),
		setStockMode: useMutation(
			orpc.bundle.setStockMode.mutationOptions({
				...options,
				onMutate: async ({ stockMode }) => {
					await queryClient.cancelQueries({ queryKey: key });
					const previous = queryClient.getQueryData(key);
					if (previous !== undefined) queryClient.setQueryData(key, { ...previous, stockMode });
					return { previous };
				},
				onError: (_error, _input, context) => {
					if (context?.previous !== undefined) queryClient.setQueryData(key, context.previous);
				},
			}),
		),
	};
}

/** Runs a write; a refusal becomes a toast in Dutch. */
function useRunBundleWrite() {
	const { t } = useTranslation("bundle");
	const { t: tCommon } = useTranslation();
	const toast = useToast();
	return async <T,>(write: Promise<T>): Promise<T | undefined> => {
		try {
			return await write;
		} catch (error) {
			const title = match(apiErrorTag(error))
				.with("BundleNotFound", () => t("error.notFound"))
				.with("ComponentVariantNotFound", () => t("error.variantNotFound"))
				.with("NestedBundle", () => t("error.nested"))
				.with("ComponentExists", () => t("error.exists"))
				.with("TooManyComponents", () => t("error.tooMany"))
				.with("ComponentNotFound", () => t("error.componentNotFound"))
				.with("OpenReservations", () => t("error.openReservations"))
				.with("BundleHasStock", () => t("error.hasStock"))
				.with("ProductArchived", () => t("error.archived"))
				.otherwise(() => tCommon("form.failed"));
			toast.add({ title, priority: "high" });
			return undefined;
		}
	};
}
